import { useEffect, useState } from 'react';

import { AppButton, ErrorBoundary, Icon } from '@ui';

import s from './RouteErrorBoundary.module.scss';

// Vite/Rolldown builds routes as separate chunks (`React.lazy`). If the app
// stays open across a deploy, the old chunk hashes get removed from the CDN:
// the next lazy import for that route 404s and throws instead of resolving.
// The failure can surface on the JS chunk *or* on the CSS the preload helper
// injects first ("Unable to preload CSS for /assets/<name>-<hash>.css"), so the
// pattern has to cover both. One silent reload picks up the fresh chunk map; a
// second failure right after means the error is real, so we stop reloading and
// show the fallback instead.
const CHUNK_LOAD_ERROR_RE =
  /dynamically imported module|importing a module script failed|failed to fetch|chunkloaderror|unable to preload css|css_chunk_load_failed/i;
const RELOAD_FLAG = 'route-error-reload-attempted';
// Il flag e' un timestamp, non un booleano: se lo lasciassimo a '1' per sempre,
// il primo deploy della sessione consumerebbe l'unico reload disponibile e ogni
// deploy successivo mostrerebbe il fallback invece di ricaricare. Ricadono nel
// fallback solo i fallimenti a ridosso del reload (= errore reale, non chunk
// stantio), evitando il loop di reload.
const RELOAD_WINDOW_MS = 15000;

function RouteErrorFallback({ error, reset, navigate }) {
  const isChunkLoadError = CHUNK_LOAD_ERROR_RE.test(error?.message || '');
  // Il reload e' un side effect: va in un effect, non nel render (leggere
  // l'orologio e scrivere un ref durante il render viola le regole di purezza
  // di React e in StrictMode verrebbe eseguito due volte). Finche' il reload e'
  // in corso non mostriamo il fallback: la pagina sta gia' andando via.
  const [reloading] = useState(() => {
    if (!isChunkLoadError) return false;
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_FLAG)) || 0;
    return Date.now() - lastReloadAt >= RELOAD_WINDOW_MS;
  });

  useEffect(() => {
    if (!reloading) return;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    window.location.reload();
  }, [reloading]);

  if (reloading) return null;

  return (
    <div className={s.root} role="alert">
      <Icon name="warning" className={s.icon} />
      <p className={s.title}>Qualcosa è andato storto</p>
      <p className={s.message}>{error?.message || 'Errore sconosciuto'}</p>
      <div className={s.actions}>
        <AppButton mode="nav" variant="primary" onClick={reset}>
          Riprova
        </AppButton>
        <AppButton
          mode="ghost"
          variant="info"
          onClick={() => {
            reset();
            navigate('/home');
          }}
        >
          Torna alla Home
        </AppButton>
      </div>
    </div>
  );
}

/**
 * Contiene un crash di render di una route dentro l'area contenuto, invece di
 * far collassare l'intero albero React (navbar/sidebar comprese) su pagina
 * bianca: `main.jsx` non ha un ErrorBoundary a monte di `<App />`.
 * `key={location.pathname}` (passata dal chiamante) resetta il boundary a
 * ogni cambio rotta, così l'errore non "segue" l'utente sulla pagina dopo.
 */
export default function RouteErrorBoundary({ children, navigate }) {
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <RouteErrorFallback error={error} reset={reset} navigate={navigate} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
